import React from 'react';
import { Divider, Paper, Stack, Typography } from '@mui/material';
import { CreateCreditApplicationInput } from '@trnsact/trnsact-shared-types';
import { formatOcaFieldValue } from 'utils/ocaFieldsCatalog';

// Field key is typed against CreateCreditApplicationInput for refactor safety
type ExtractionKey = keyof CreateCreditApplicationInput;

interface ConfidenceField {
  label: string;
  key: ExtractionKey;
  format?: (v: unknown) => string;
}

// Dynamics CRM optionset codes for tib (time in business) — not part of the OCA form, so it can't
// be derived from fields_sectionized.json; must stay in sync with the CRM field
const TIME_IN_BUSINESS_LABELS: Record<string, string> = {
  '803370000': 'Pre-Startup',
  '803370001': '0-6 Months',
  '803370002': '6-12 Months',
  '803370003': '1-2 Years',
  '803370004': '2-3 Years',
  '803370005': '3-5 Years',
  '803370006': '5-10 Years',
  '803370007': '10+ Years',
};
const timeInBusiness = (v: unknown) => TIME_IN_BUSINESS_LABELS[String(v)] ?? String(v);

// Labels sourced from DynamicOCA/config/fields_sectionized.json displayLabel.en. Formatting for
// fields without an explicit override is derived from that same catalog (see formatOcaFieldValue)
// so the displayed value matches the UI component that originally captured it.
const EXTRACTION_FIELDS: ConfidenceField[] = [
  { label: 'Legal Entity Name', key: 'businessName' },
  { label: 'First Name', key: 'firstName' },
  { label: 'Last Name', key: 'lastName' },
  { label: 'Email', key: 'email' },
  { label: 'Phone Number', key: 'phone' },
  { label: 'Business Phone', key: 'bizPhone' },
  { label: 'Legal Structure', key: 'legalStructure' },
  { label: 'Business Description', key: 'businessType' },
  { label: 'DBA', key: 'dba' },
  { label: 'Physical Business Address', key: 'address' },
  { label: 'City', key: 'city' },
  { label: 'State', key: 'state' },
  { label: 'Postal Code', key: 'postalCode' },
  { label: 'County', key: 'addressCounty' },
  { label: 'Country', key: 'country' },
  { label: 'Equipment Financing Amount Requested', key: 'amountRequested' },
  { label: 'Annual Revenue', key: 'annualRevenue' },
  { label: 'Term Requested', key: 'termRequested' },
  { label: 'Time in Business', key: 'tib', format: timeInBusiness },
  { label: 'Number of Employees', key: 'numOfEmployees' },
  { label: 'Equipment Make and Model', key: 'ccg_transaction_equipmentdescription' },
];

function confidenceColor(score: number): string {
  if (score >= 0.85) return '#4caf50';
  if (score >= 0.65) return '#ffa726';
  return '#ef5350';
}

interface ExtractionRowProps {
  label: string;
  value: string;
  confidence: number | null;
}

const ExtractionRow = ({ label, value, confidence }: ExtractionRowProps) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="baseline">
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1 }}>
      {value}
    </Typography>
    {confidence != null && (
      <Typography variant="caption" sx={{ flexShrink: 0, color: confidenceColor(confidence) }}>
        {Math.round(confidence * 100)}%
      </Typography>
    )}
  </Stack>
);

interface CreditAppExtractionSectionProps {
  payload: Record<string, unknown> | null | undefined;
}

export const CreditAppExtractionSection = ({ payload }: CreditAppExtractionSectionProps) => {
  if (!payload) return null;

  const extraction = payload.detailed_extraction as Record<string, unknown> | null | undefined;
  const dealerAnalysis = payload.dealer_analysis as Record<string, unknown> | null | undefined;
  const dealerMatch = payload.dealer_match as
    | { dynamicsAccountId: string | null; confidenceScore: number; found: boolean; reasoning: string }
    | null
    | undefined;

  const extractionRows = extraction
    ? EXTRACTION_FIELDS.flatMap(({ label, key, format }) => {
        const node = extraction[key] as { value: unknown; confidenceScore: number } | null | undefined;
        if (!node?.value) return [];
        const formatted = format ? format(node.value) : formatOcaFieldValue(key, node.value);
        return [{ label, value: formatted, confidence: node.confidenceScore ?? null }];
      })
    : [];

  const owners = extraction?.owners as Array<unknown> | null | undefined;
  const ownerCount = Array.isArray(owners) ? owners.length : 0;

  const hasDealerAnalysis = dealerAnalysis && Object.keys(dealerAnalysis).length > 0;
  const hasDealerMatch = dealerMatch && Object.keys(dealerMatch).length > 0;

  if (extractionRows.length === 0 && !hasDealerAnalysis && !hasDealerMatch) return null;

  return (
    <>
      {hasDealerAnalysis && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Dealer &amp; Applicant Analysis
          </Typography>
          <Stack>
            {[
              { label: 'Applicant', key: 'applicantName' },
              { label: 'Applicant Type', key: 'applicantType' },
              { label: 'Dealer', key: 'dealerName' },
              { label: 'Dealer Address', key: 'dealerAddress' },
            ]
              .filter(({ key }) => dealerAnalysis![key])
              .map(({ label, key }, index, arr) => (
                <React.Fragment key={key}>
                  <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="baseline">
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
                      {label}
                    </Typography>
                    <Typography variant="body2">{dealerAnalysis![key] as string}</Typography>
                    {key === 'dealerName' && dealerAnalysis!.confidenceScore != null && (
                      <Typography
                        variant="caption"
                        sx={{ flexShrink: 0, color: confidenceColor(dealerAnalysis!.confidenceScore as number) }}
                      >
                        {Math.round((dealerAnalysis!.confidenceScore as number) * 100)}%
                      </Typography>
                    )}
                  </Stack>
                  {index < arr.length - 1 && <Divider />}
                </React.Fragment>
              ))}

            {!!dealerAnalysis?.reasoning && (
              <>
                <Divider />

                <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
                    Reasoning
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {dealerAnalysis!.reasoning as string}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {hasDealerMatch && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Dealer Match Result
          </Typography>
          <Stack>
            <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="baseline">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
                Existing Dealer
              </Typography>
              <Typography variant="body2">
                {dealerMatch!.found && dealerMatch!.dynamicsAccountId
                  ? dealerMatch!.dynamicsAccountId
                  : 'No confident match found'}
              </Typography>
              {dealerMatch!.confidenceScore != null && (
                <Typography
                  variant="caption"
                  sx={{ flexShrink: 0, color: confidenceColor(dealerMatch!.confidenceScore) }}
                >
                  {Math.round(dealerMatch!.confidenceScore * 100)}%
                </Typography>
              )}
            </Stack>
            {dealerMatch!.reasoning && (
              <>
                <Divider />
                <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
                    Reasoning
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {dealerMatch!.reasoning}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {extractionRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Parsed Data Summary
            </Typography>
            <Typography variant="caption" color="text.secondary">
              confidence
            </Typography>
          </Stack>
          <Stack>
            {extractionRows.map(({ label, value, confidence }, index) => (
              <React.Fragment key={label}>
                {index > 0 && <Divider />}
                <ExtractionRow label={label} value={value} confidence={confidence} />
              </React.Fragment>
            ))}
            {ownerCount > 0 && (
              <>
                <Divider />
                <Stack direction="row" spacing={1} sx={{ py: 0.75 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
                    Owners / Guarantors
                  </Typography>
                  <Typography variant="body2">{ownerCount}</Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      )}
    </>
  );
};
